/**
 * User Activity Tracker
 * Records last 3-4 user actions for bug reporting context
 */

class UserActivityTracker {
  constructor() {
    this.maxSteps = 4;
    this.activities = [];
    this.sessionKey = 'user_activity_steps';
    this.isTracking = false;
    
    // Load from sessionStorage on init
    this.loadFromSession();
  }

  /**
   * Start tracking user activities
   */
  start() {
    if (this.isTracking) return;
    
    this.isTracking = true;
    
    // Track route changes
    this.trackRouteChanges();
    
    // Track clicks
    document.addEventListener('click', this.handleClick.bind(this), true);
    
    // Track form submissions
    document.addEventListener('submit', this.handleSubmit.bind(this), true);
    
  }

  /**
   * Stop tracking
   */
  stop() {
    this.isTracking = false;
    document.removeEventListener('click', this.handleClick.bind(this), true);
    document.removeEventListener('submit', this.handleSubmit.bind(this), true);
  }

  /**
   * Track route changes
   */
  trackRouteChanges() {
    // Store original pushState and replaceState
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;
    
    const trackNavigation = (url) => {
      this.addActivity({
        action: 'navigation',
        details: `Navigated to ${url}`,
        url: url
      });
    };
    
    // Override pushState
    window.history.pushState = function(...args) {
      originalPushState.apply(window.history, args);
      trackNavigation(window.location.pathname);
    };
    
    // Override replaceState
    window.history.replaceState = function(...args) {
      originalReplaceState.apply(window.history, args);
      trackNavigation(window.location.pathname);
    };
    
    // Listen to popstate (back/forward buttons)
    window.addEventListener('popstate', () => {
      trackNavigation(window.location.pathname);
    });
  }

  /**
   * Handle click events
   */
  handleClick(event) {
    const target = event.target;
    const element = target.closest('button, a, [role="button"]');
    
    if (!element) return;
    
    const elementInfo = this.getElementInfo(element);
    const actionText = element.textContent?.trim().substring(0, 50) || 'Unknown';
    
    this.addActivity({
      action: 'click',
      details: `Clicked: ${actionText}`,
      element: elementInfo,
      url: window.location.pathname
    });
  }

  /**
   * Handle form submissions
   */
  handleSubmit(event) {
    const form = event.target;
    const formName = form.name || form.id || 'Unnamed form';
    
    this.addActivity({
      action: 'form_submit',
      details: `Submitted form: ${formName}`,
      url: window.location.pathname
    });
  }

  /**
   * Get meaningful element information
   */
  getElementInfo(element) {
    const info = {
      tag: element.tagName.toLowerCase(),
      id: element.id || null,
      className: element.className || null,
      text: element.textContent?.trim().substring(0, 50) || null
    };
    
    return info;
  }

  /**
   * Add activity to the tracker
   */
  addActivity(activity) {
    const step = {
      ...activity,
      timestamp: new Date().toISOString()
    };
    
    // Add to beginning of array
    this.activities.unshift(step);
    
    // Keep only last N activities
    if (this.activities.length > this.maxSteps) {
      this.activities = this.activities.slice(0, this.maxSteps);
    }
    
    // Save to sessionStorage
    this.saveToSession();
  }

  /**
   * Get recent activities in readable format
   */
  getRecentSteps() {
    return this.activities.map((activity, index) => {
      const stepNumber = this.activities.length - index;
      return `${stepNumber}. ${activity.details} (${this.formatTime(activity.timestamp)})`;
    }).reverse();
  }

  /**
   * Get raw activity data
   */
  getRawActivities() {
    return [...this.activities].reverse();
  }

  /**
   * Format timestamp to readable time
   */
  formatTime(timestamp) {
    const date = new Date(timestamp);
    return date.toLocaleTimeString();
  }

  /**
   * Save to sessionStorage
   */
  saveToSession() {
    try {
      sessionStorage.setItem(this.sessionKey, JSON.stringify(this.activities));
    } catch (error) {
      console.error('Failed to save activities to session:', error);
    }
  }

  /**
   * Load from sessionStorage
   */
  loadFromSession() {
    try {
      const stored = sessionStorage.getItem(this.sessionKey);
      if (stored) {
        this.activities = JSON.parse(stored);
      }
    } catch (error) {
      console.error('Failed to load activities from session:', error);
      this.activities = [];
    }
  }

  /**
   * Clear all activities
   */
  clear() {
    this.activities = [];
    sessionStorage.removeItem(this.sessionKey);
  }
}

// Create singleton instance
const activityTracker = new UserActivityTracker();

export default activityTracker;
